
import React, { useEffect, useState, useCallback } from 'react';
import { ref, set, get, update, remove } from 'firebase/database';
import { realtimeDb } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import { 
  FiEye, FiEyeOff, FiTrash2, FiEdit, FiLogOut, FiArrowLeft, 
  FiSave, FiUserPlus, FiInfo, FiShield, FiHome, FiBriefcase, 
  FiMail, FiPhone, FiKey, FiFileText, FiPlus, FiMinus, FiImage 
} from 'react-icons/fi';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  normalizeCompanyKey,
  firebaseCompanyKey,
  getTestReportKey,
  ALL_COMPANIES,
  DEFAULT_COMPANIES
} from '../../Utils/companyUtils';

Modal.setAppElement('#root');

const kvaOptions = [100, 63, 25, 16, 10];

const AdminProfile = () => {
  const navigate = useNavigate();

  // State variables
  const [companyList, setCompanyList] = useState([]);
  const [adminMobile, setAdminMobile] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [company, setCompany] = useState('');
  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [companyUsers, setCompanyUsers] = useState([]);
  const [showPasswords, setShowPasswords] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({
    address: '',
    gst: '',
    tender: '',
    email: '',
    logo: '' // Added logo field
  });
  const [logoPreview, setLogoPreview] = useState(null); // For logo preview
  const [activeTab, setActiveTab] = useState('users');
  const [isLoading, setIsLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteAdminPassword, setDeleteAdminPassword] = useState('');
  
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [newUserPassword, setNewUserPassword] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Test Report State
  const [selectedKva, setSelectedKva] = useState('');
  const [testReports, setTestReports] = useState({});
  const [currentReport, setCurrentReport] = useState({
    voltageRatio: [['', '', ''], ['', '', ''], ['', '', ''], ['', '', ''], ['', '', ''], ['', '', '']],
    megger: [['', '', ''], ['', '', ''], ['', '', '']],
    shortCircuit: [['', '', ''], ['', '', ''], ['', '', ''], ['', '', ''], ['', '', ''], ['', '', '']],
    noOfCharging: [['', '', ''], ['', '', ''], ['', '', ''], ['', '', '']]
  });

  // Handle logo file upload
  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!['image/png', 'image/jpeg'].includes(file.type)) {
        toast.error('Please upload a PNG or JPG image');
        return;
      }
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        toast.error('Image size should not exceed 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setCompanyInfo({ ...companyInfo, logo: reader.result });
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Fetch all data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [companySnap, adminSnap, reportsSnap] = await Promise.all([
          get(ref(realtimeDb, 'companies')),
          get(ref(realtimeDb, 'power/admin')),
          get(ref(realtimeDb, 'testReports'))
        ]);

        if (companySnap.exists()) {
          const data = companySnap.val();
          const firebaseCompanies = Object.keys(data).map(normalizeCompanyKey);
          const combinedCompanies = [...new Set([...ALL_COMPANIES, ...firebaseCompanies])];
          setCompanyList(combinedCompanies);
        }

        if (adminSnap.exists()) {
          const data = adminSnap.val();
          setAdminMobile(data.mobile || '');
          setAdminPassword(data.password || '');
        }

        if (reportsSnap.exists()) {
          setTestReports(reportsSnap.val());
        }
      } catch (error) {
        toast.error('Failed to load data');
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Load test report when company or kVA changes
  useEffect(() => {
    if (company && selectedKva) {
      const reportKey = getTestReportKey(company, selectedKva);
      if (testReports[reportKey]) {
        setCurrentReport(testReports[reportKey]);
      } else {
        setCurrentReport({
          voltageRatio: [['', '', ''], ['', '', ''], ['', '', ''], ['', '', ''], ['', '', ''], ['', '', '']],
          megger: [['', '', ''], ['', '', ''], ['', '', '']],
          shortCircuit: [['', '', ''], ['', '', ''], ['', '', ''], ['', '', ''], ['', '', ''], ['', '', '']],
          noOfCharging: [['', '', ''], ['', '', ''], ['', '', ''], ['', '', '']]
        });
      }
    }
  }, [company, selectedKva, testReports]);

  // Fetch company users
  const fetchCompanyUsers = useCallback(async () => {
    if (!company) return;
    
    try {
      const normalizedCompany = normalizeCompanyKey(company);
      const snap = await get(ref(realtimeDb, 'users'));
      if (snap.exists()) {
        const data = snap.val();
        const filtered = Object.entries(data)
          .filter(([_, val]) => normalizeCompanyKey(val.company) === normalizedCompany)
          .map(([id, val]) => ({ id, ...val }));
        setCompanyUsers(filtered);
      } else {
        setCompanyUsers([]);
      }
    } catch (error) {
      toast.error('Failed to fetch users');
      console.error(error);
    }
  }, [company]);

  useEffect(() => {
    if (company) {
      fetchCompanyUsers();
      const fetchCompanyInfo = async () => {
        try {
          const normalizedCompany = normalizeCompanyKey(company);
          const snap = await get(ref(realtimeDb, `companyDetails/${normalizedCompany}`));
          const data = snap.exists() ? snap.val() : {
            address: '',
            gst: '',
            tender: '',
            email: '',
            logo: ''
          };
          setCompanyInfo(data);
          setLogoPreview(data.logo || null);
        } catch (error) {
          toast.error('Failed to fetch company info');
          console.error(error);
        }
      };
      fetchCompanyInfo();
    }
  }, [company, fetchCompanyUsers]);

  // Admin functions
  const updateAdminMobile = async () => {
    try {
      await update(ref(realtimeDb, 'power/admin'), { mobile: adminMobile });
      toast.success('Mobile updated successfully');
    } catch (error) {
      toast.error('Failed to update mobile');
      console.error(error);
    }
  };

  const updateAdminPassword = async () => {
    try {
      await update(ref(realtimeDb, 'power/admin'), { password: adminPassword });
      toast.success('Password updated successfully');
    } catch (error) {
      toast.error('Failed to update password');
      console.error(error);
    }
  };

  // User management functions
  const handleCreateUser = async () => {
    if (!company || !mobile || !password) {
      toast.warning('Please fill all fields');
      return;
    }

    try {
      const normalizedCompany = normalizeCompanyKey(company);
      await set(ref(realtimeDb, `users/${mobile}`), {
        company: normalizedCompany,
        password,
        phone: mobile,
        createdAt: new Date().toISOString(),
      });
      toast.success('User created successfully');
      setMobile('');
      setPassword('');
      fetchCompanyUsers();
    } catch (error) {
      toast.error('Failed to create user');
      console.error('Error creating user:', error);
    }
  };

  const openResetPasswordModal = (id) => {
    setUserToReset(id);
    setNewUserPassword('');
    setResetPasswordModalOpen(true);
  };

  const handleUpdateUserPassword = async () => {
    if (!newUserPassword) {
      toast.warning('Please enter a new password');
      return;
    }
    
    try {
      await update(ref(realtimeDb, `users/${userToReset}`), { password: newUserPassword });
      toast.success('Password updated');
      setResetPasswordModalOpen(false);
      fetchCompanyUsers();
    } catch (error) {
      toast.error('Failed to update password');
      console.error(error);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await set(ref(realtimeDb, `users/${id}`), null);
      toast.success('User deleted successfully');
      fetchCompanyUsers();
    } catch (error) {
      toast.error('Failed to delete user');
      console.error(error);
    }
  };

  // Company functions
  const handleDeleteCompany = async () => {
    if (!company) return;
    
    if (!deleteAdminPassword || deleteAdminPassword !== adminPassword) {
      toast.error('Incorrect admin password');
      return;
    }

    try {
      setIsLoading(true);
      const normalizedCompany = normalizeCompanyKey(company);
      const companyKey = firebaseCompanyKey(normalizedCompany);

      await Promise.all([
        remove(ref(realtimeDb, `companies/${companyKey}`)),
        remove(ref(realtimeDb, `companyDetails/${normalizedCompany}`)),
        remove(ref(realtimeDb, `kvaPricing/${normalizedCompany}`)),
        remove(ref(realtimeDb, `entryPass/${normalizedCompany}`)),
        remove(ref(realtimeDb, `billSummary/${normalizedCompany}`)),
        remove(ref(realtimeDb, `info/${normalizedCompany}`)),
      ]);

      const testReportsSnap = await get(ref(realtimeDb, 'testReports'));
      if (testReportsSnap.exists()) {
        const testReports = testReportsSnap.val();
        const updates = {};
        Object.keys(testReports).forEach((key) => {
          if (key.startsWith(`${companyKey}_`)) {
            updates[`testReports/${key}`] = null;
          }
        });
        await update(ref(realtimeDb), updates);
      }

      const usersSnap = await get(ref(realtimeDb, 'users'));
      if (usersSnap.exists()) {
        const users = usersSnap.val();
        const updates = {};
        Object.entries(users).forEach(([key, val]) => {
          if (normalizeCompanyKey(val.company) === normalizedCompany) {
            updates[`users/${key}`] = null;
          }
        });
        await update(ref(realtimeDb), updates);
      }

      setCompanyList(prev => prev.filter(c => normalizeCompanyKey(c) !== normalizedCompany));
      setCompany('');
      setDeleteModalOpen(false);
      setDeleteAdminPassword('');
      toast.success(`${normalizedCompany} deleted successfully`);
    } catch (err) {
      toast.error('Failed to delete company');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCompanyInfo = async () => {
    try {
      const normalizedCompany = normalizeCompanyKey(company);
      await set(ref(realtimeDb, `companyDetails/${normalizedCompany}`), companyInfo);
      toast.success('Company info saved successfully');
    } catch (err) {
      toast.error('Failed to save company info');
      console.error(err);
    }
  };

  // Test Report functions
  const handleTestDataChange = (section, rowIndex, colIndex, value) => {
    setCurrentReport(prev => {
      const updatedSection = prev[section].map((row, rIdx) => {
        if (rIdx === rowIndex) {
          const newRow = [...row];
          newRow[colIndex] = value;
          return newRow;
        }
        return row;
      });
      return { ...prev, [section]: updatedSection };
    });
  };

  const saveTestReport = async () => {
    if (!company || !selectedKva) {
      toast.warning('Please select both company and kVA');
      return;
    }

    try {
      const reportKey = getTestReportKey(company, selectedKva);
      await set(ref(realtimeDb, `testReports/${reportKey}`), currentReport);
      toast.success('Test report saved successfully');
      
      setTestReports(prev => ({
        ...prev,
        [reportKey]: currentReport
      }));
    } catch (error) {
      toast.error('Failed to save test report');
      console.error(error);
    }
  };

  const addRow = (section) => {
    setCurrentReport(prev => ({
      ...prev,
      [section]: [...prev[section], ['', '', '']]
    }));
  };

  const removeRow = (section, rowIndex) => {
    if (currentReport[section].length <= 1) return;
    
    setCurrentReport(prev => {
      const updatedSection = prev[section].filter((_, idx) => idx !== rowIndex);
      return { ...prev, [section]: updatedSection };
    });
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/');
    toast.info('Logged out successfully');
  };

  // Render Test Report Section
  const renderTestSection = (title, sectionKey, rows) => {
    return (
      <div className="mb-8">
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full border">
            <thead>
              <tr>
                <th className="border px-2 py-1">#</th>
                <th className="border px-2 py-1">1</th>
                <th className="border px-2 py-1">2</th>
                <th className="border px-2 py-1">3</th>
                <th className="border px-2 py-1">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  <td className="border px-2 py-1">{rowIndex + 1}</td>
                  {row.map((cell, colIndex) => (
                    <td className="border px-2 py-1" key={colIndex}>
                      <input
                        type="text"
                        className="w-full p-1 border-0 focus:ring-1 focus:ring-blue-500"
                        value={cell}
                        onChange={(e) => 
                          handleTestDataChange(sectionKey, rowIndex, colIndex, e.target.value)
                        }
                      />
                    </td>
                  ))}
                  <td className="border px-2 py-1">
                    <button
                      onClick={() => removeRow(sectionKey, rowIndex)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Remove row"
                    >
                      <FiMinus />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button
          onClick={() => addRow(sectionKey)}
          className="mt-2 bg-blue-100 text-blue-700 px-3 py-1 rounded flex items-center text-sm"
        >
          <FiPlus className="mr-1" /> Add Row
        </button>
      </div>
    );
  };

  // Check if company is default (protected from deletion)
  const isDefaultCompany = DEFAULT_COMPANIES.includes(company);

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {/* Sidebar */}
      <aside className={`w-72 bg-slate-900 text-white flex flex-col transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-50 lg:static lg:translate-x-0 shadow-2xl shrink-0`}>
        <div className="p-6 flex items-center justify-between border-b border-slate-800">
          <h1 className="text-xl font-bold flex items-center gap-3">
            <div className="bg-blue-600 p-2 rounded-lg">
              <FiShield className="text-white" />
            </div>
            Admin Panel
          </h1>
          <button className="lg:hidden text-gray-400 hover:text-white" onClick={() => setSidebarOpen(false)}>
            <FiArrowLeft className="text-2xl" />
          </button>
        </div>

        <div className="p-6 border-b border-slate-800">
          <label className="text-xs text-slate-400 uppercase tracking-widest font-semibold mb-3 block">Select Company</label>
          <div className="relative">
            <FiBriefcase className="absolute left-3 top-3.5 text-slate-400" />
            <select
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-white appearance-none cursor-pointer hover:bg-slate-700 transition-colors"
            >
              <option value="">-- Choose Company --</option>
              {companyList.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </div>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <button
            onClick={() => { setActiveTab('users'); if(window.innerWidth < 1024) setSidebarOpen(false); }}
            className={`w-full text-left px-4 py-3.5 rounded-xl flex items-center transition-all duration-200 ${activeTab === 'users' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50 font-medium' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
          >
            <FiUserPlus className="mr-3 text-lg" />
            Manage Users
          </button>
          <button
            onClick={() => { setActiveTab('company'); if(window.innerWidth < 1024) setSidebarOpen(false); }}
            className={`w-full text-left px-4 py-3.5 rounded-xl flex items-center transition-all duration-200 ${activeTab === 'company' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50 font-medium' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
          >
            <FiHome className="mr-3 text-lg" />
            Company Details
          </button>
          <button
            onClick={() => { setActiveTab('reports'); if(window.innerWidth < 1024) setSidebarOpen(false); }}
            className={`w-full text-left px-4 py-3.5 rounded-xl flex items-center transition-all duration-200 ${activeTab === 'reports' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50 font-medium' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
          >
            <FiFileText className="mr-3 text-lg" />
            Test Reports
          </button>
          <button
            onClick={() => { setActiveTab('admin'); if(window.innerWidth < 1024) setSidebarOpen(false); }}
            className={`w-full text-left px-4 py-3.5 rounded-xl flex items-center transition-all duration-200 ${activeTab === 'admin' ? 'bg-blue-600 text-white shadow-lg shadow-blue-900/50 font-medium' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
          >
            <FiInfo className="mr-3 text-lg" />
            Admin Settings
          </button>
        </nav>

        <div className="p-4 border-t border-slate-800">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 bg-red-500/10 text-red-400 px-4 py-3 rounded-xl hover:bg-red-500/20 hover:text-red-300 transition-colors font-medium"
          >
            <FiLogOut />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden">
        {/* Top Header */}
        <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 p-4 flex items-center justify-between z-10 shrink-0 sticky top-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-600 hover:text-blue-600 p-2 bg-gray-100 rounded-lg transition-colors"
            >
              <FiBriefcase className="text-xl" />
            </button>
            <button
              onClick={() => navigate(-1)}
              className="hidden lg:flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-colors bg-gray-50 hover:bg-blue-50 px-4 py-2 rounded-xl border border-gray-200"
            >
              <FiArrowLeft />
              <span className="font-medium">Back to Portal</span>
            </button>
            <div className="h-6 w-px bg-gray-300 mx-2 hidden lg:block"></div>
            <h2 className="text-xl font-bold text-gray-800 ml-2 lg:ml-0 flex items-center gap-2">
              {activeTab === 'users' && <><FiUserPlus className="text-blue-600"/> User Management</>}
              {activeTab === 'company' && <><FiHome className="text-blue-600"/> Company Details</>}
              {activeTab === 'reports' && <><FiFileText className="text-blue-600"/> Test Reports</>}
              {activeTab === 'admin' && <><FiShield className="text-blue-600"/> Admin Settings</>}
            </h2>
          </div>
        </header>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-gray-50/50">
          <div className="max-w-6xl mx-auto space-y-6">
          {/* Users Tab */}
          {activeTab === 'users' && (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <FiUserPlus className="mr-2 text-blue-600" />
                  Manage Users {company && `for ${company}`}
                </h2>
                {company && !isDefaultCompany && (
                  <button
                    onClick={() => setDeleteModalOpen(true)}
                    className="bg-red-100 text-red-700 px-4 py-2 rounded-lg hover:bg-red-200 flex items-center"
                  >
                    <FiTrash2 className="mr-2" />
                    Delete Company
                  </button>
                )}
              </div>

              {company ? (
                <>
                  <div className="grid md:grid-cols-3 gap-4 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                        <FiPhone className="mr-2" />
                        Mobile
                      </label>
                      <input
                        value={mobile}
                        onChange={(e) => setMobile(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg"
                        placeholder="User mobile"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                        <FiKey className="mr-2" />
                        Password
                      </label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg"
                        placeholder="User password"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={handleCreateUser}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center w-full justify-center"
                      >
                        <FiUserPlus className="mr-2" />
                        Create User
                      </button>
                    </div>
                  </div>

                  <div className="mb-4 flex justify-between items-center">
                    <h3 className="font-medium text-gray-700">User List</h3>
                    <label className="inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showPasswords}
                        onChange={() => setShowPasswords(!showPasswords)}
                        className="sr-only peer"
                      />
                      <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      <span className="ml-2 text-sm font-medium text-gray-700">
                        {showPasswords ? (
                          <span className="flex items-center">
                            <FiEyeOff className="mr-1" /> Hide Passwords
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <FiEye className="mr-1" /> Show Passwords
                          </span>
                        )}
                      </span>
                    </label>
                  </div>

                  {companyUsers.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No users found for this company
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Password</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {companyUsers.map((user) => (
                            <tr key={user.id}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.phone}</td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {showPasswords ? user.password : '••••••••'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {new Date(user.createdAt).toLocaleDateString()}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <button
                                  onClick={() => openResetPasswordModal(user.id)}
                                  className="text-blue-600 hover:text-blue-900 mr-4 flex items-center"
                                >
                                  <FiEdit className="mr-1" /> Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(user.id)}
                                  className="text-red-600 hover:text-red-900 flex items-center"
                                >
                                  <FiTrash2 className="mr-1" /> Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FiInfo className="mx-auto text-3xl mb-3" />
                  <p>Please select a company to manage users</p>
                </div>
              )}
            </div>
          )}

          {/* Company Details Tab */}
          {activeTab === 'company' && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-6 text-gray-800 flex items-center">
                <FiHome className="mr-2 text-blue-600" />
                Company Details {company && `for ${company}`}
              </h2>

              {company ? (
                <>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                      <input
                        type="text"
                        className="w-full p-2 border border-gray-300 rounded-lg"
                        value={companyInfo.address || ''}
                        onChange={(e) =>
                          setCompanyInfo({ ...companyInfo, address: e.target.value })
                        }
                        placeholder="Company address"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">GST Number</label>
                      <input
                        type="text"
                        className="w-full p-2 border border-gray-300 rounded-lg"
                        value={companyInfo.gst || ''}
                        onChange={(e) =>
                          setCompanyInfo({ ...companyInfo, gst: e.target.value })
                        }
                        placeholder="GST number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tender Number</label>
                      <input
                        type="text"
                        className="w-full p-2 border border-gray-300 rounded-lg"
                        value={companyInfo.tender || ''}
                        onChange={(e) =>
                          setCompanyInfo({ ...companyInfo, tender: e.target.value })
                        }
                        placeholder="Tender number"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                        <FiMail className="mr-2" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        className="w-full p-2 border border-gray-300 rounded-lg"
                        value={companyInfo.email || ''}
                        onChange={(e) =>
                          setCompanyInfo({ ...companyInfo, email: e.target.value })
                        }
                        placeholder="Company email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                        <FiImage className="mr-2" />
                        Company Logo
                      </label>
                      <input
                        type="file"
                        accept="image/png,image/jpeg"
                        onChange={handleLogoUpload}
                        className="w-full p-2 border border-gray-300 rounded-lg"
                      />
                      {logoPreview && (
                        <div className="mt-2">
                          <img
                            src={logoPreview}
                            alt="Company Logo Preview"
                            className="h-20 w-20 object-contain border border-gray-300 rounded"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <button
                    onClick={handleSaveCompanyInfo}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
                  >
                    <FiSave className="mr-2" />
                    Save Company Info
                  </button>
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FiInfo className="mx-auto text-3xl mb-3" />
                  <p>Please select a company to view/edit details</p>
                </div>
              )}
            </div>
          )}

          {/* Test Reports Tab */}
          {activeTab === 'reports' && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-6 text-gray-800 flex items-center">
                <FiFileText className="mr-2 text-blue-600" />
                Test Reports {company && `for ${company}`}
              </h2>

              {company ? (
                <>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Select kVA
                    </label>
                    <select
                      value={selectedKva}
                      onChange={(e) => setSelectedKva(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Select kVA</option>
                      {kvaOptions.map((kva) => (
                        <option key={kva} value={kva}>
                          {kva} kVA
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedKva ? (
                    <>
                      {renderTestSection('VOLTAGE RATIO', 'voltageRatio', currentReport.voltageRatio)}
                      {renderTestSection('MEGGER', 'megger', currentReport.megger)}
                      {renderTestSection('SHORT CIRCUIT', 'shortCircuit', currentReport.shortCircuit)}
                      {renderTestSection('NO OF CHARGING', 'noOfCharging', currentReport.noOfCharging)}

                      <button
                        onClick={saveTestReport}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
                      >
                        <FiSave className="mr-2" />
                        Save Test Report
                      </button>
                    </>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      Please select a kVA to view/edit test reports
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FiInfo className="mx-auto text-3xl mb-3" />
                  <p>Please select a company to manage test reports</p>
                </div>
              )}
            </div>
          )}

          {/* Admin Settings Tab */}
          {activeTab === 'admin' && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-6 text-gray-800 flex items-center">
                <FiShield className="mr-2 text-blue-600" />
                Admin Settings
              </h2>
              
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <FiPhone className="mr-2" />
                    Mobile Number
                  </label>
                  <input
                    value={adminMobile}
                    onChange={(e) => setAdminMobile(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Admin mobile number"
                  />
                  <button
                    onClick={updateAdminMobile}
                    className="mt-3 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                  >
                    <FiSave className="mr-2" />
                    Update Mobile
                  </button>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    <FiKey className="mr-2" />
                    Password
                  </label>
                  <input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => setAdminPassword(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Admin password"
                  />
                  <button
                    onClick={updateAdminPassword}
                    className="mt-3 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                  >
                    <FiSave className="mr-2" />
                    Update Password
                  </button>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>
      </main>

      {/* Delete Company Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onRequestClose={() => setDeleteModalOpen(false)}
        className="modal"
        overlayClassName="modal-overlay"
        style={{
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            border: 'none',
            background: 'transparent',
            padding: 0,
            width: '100%',
            maxWidth: '450px'
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000
          }
        }}
      >
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full">
          <h2 className="text-2xl font-bold mb-4 text-red-600 flex items-center gap-3">
            <div className="bg-red-100 p-2 rounded-full">
              <FiTrash2 className="text-red-600" />
            </div>
            Delete Company
          </h2>
          <p className="mb-6 text-gray-600">
            Are you sure you want to delete <strong className="text-gray-900">{company}</strong>? This will permanently remove all company data, test reports, and associated users.
          </p>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Enter Admin Password to Confirm
            </label>
            <input
              type="password"
              value={deleteAdminPassword}
              onChange={(e) => setDeleteAdminPassword(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
              placeholder="Admin password"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setDeleteModalOpen(false)}
              className="px-5 py-2.5 font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteCompany}
              disabled={isLoading || !deleteAdminPassword}
              className="px-5 py-2.5 font-medium bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {isLoading ? 'Deleting...' : 'Confirm Delete'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={resetPasswordModalOpen}
        onRequestClose={() => setResetPasswordModalOpen(false)}
        className="modal"
        overlayClassName="modal-overlay"
        style={{
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            border: 'none',
            background: 'transparent',
            padding: 0,
            width: '100%',
            maxWidth: '400px'
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000
          }
        }}
      >
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full">
          <h2 className="text-2xl font-bold mb-6 text-gray-800 flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-full">
              <FiKey className="text-blue-600" />
            </div>
            Reset Password
          </h2>
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              New Password for User
            </label>
            <input
              type="text"
              value={newUserPassword}
              onChange={(e) => setNewUserPassword(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder="Enter new password"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setResetPasswordModalOpen(false)}
              className="px-5 py-2.5 font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleUpdateUserPassword}
              disabled={!newUserPassword}
              className="px-5 py-2.5 font-medium bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Save Password
            </button>
          </div>
        </div>
      </Modal>

    </div>
  );
};

export default AdminProfile;
